import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { useClientReady } from '../../../../util/useClientReady';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import ExploreMenu from '../ExploreMenu';
import NavigationIcon from '../NavigationIcon';

import imagotipoImg from '../../../../assets/logos/uroborox_v color_6.png';
import css from './TopbarDesktop.module.css';

const InboxLink = ({ notificationCount, inboxTab, intl }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      id="inbox-link"
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: inboxTab }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink, intl, notificationCount = 0, inboxTab }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <Menu skipFocusOnNavigation={true}>
      <MenuLabel
        id="profile-menu-label"
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}
      >
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
        {notificationCount > 0 ? <span className={css.profileUnread}>{notificationCount}</span> : null}
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="InboxPage">
          <NamedLink name="InboxPage" params={{ tab: inboxTab }} className={css.menuLink}>
            <FormattedMessage id="TopbarDesktop.inbox" />
            {notificationCount > 0 ? <span className={css.menuUnread}>{notificationCount}</span> : null}
          </NamedLink>
        </MenuItem>
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="soporte">
          <a className={css.menuLink} href="mailto:soporte@uroborox.io?subject=Soporte%20Uroborox">
            <span className={css.menuItemBorder} />
            <FormattedMessage id="Topbar.support" />
          </a>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const TopbarDesktop = props => {
  const {
    className,
    config,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    showCreateListingsLink,
    inboxTab,
  } = props;
  const mounted = useClientReady();

  const authenticatedOnClientSide = mounted && isAuthenticated;

  // TOPBAR-UNIFY: topbar sólido en todas las páginas; sin estados translúcidos
  // de scroll. El autoescondido (translateY) lo gestiona useHideOnScroll fuera.
  const classes = classNames(rootClassName || css.root, className);

  const createListingLinkMaybe =
    authenticatedOnClientSide && showCreateListingsLink ? (
      <NamedLink name="NewListingPage" className={css.topbarLinkCta}>
        <span className={css.topbarLinkLabel}>
          <FormattedMessage id="TopbarDesktop.createListing" />
        </span>
      </NamedLink>
    ) : null;

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} intl={intl} />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      notificationCount={notificationCount}
      inboxTab={inboxTab}
      showManageListingsLink={showCreateListingsLink}
      intl={intl}
    />
  ) : null;

  // Unauthenticated: preserve publication and an accessible account shortcut.
  const authLinks = authenticatedOnClientSide ? null : (
    <>
      <NamedLink name="NewListingPage" className={css.topbarLinkCta}>
        <span className={css.topbarLinkLabel}>
          <FormattedMessage id="TopbarDesktop.createListing" />
        </span>
      </NamedLink>
      <NamedLink
        name="LoginPage"
        className={css.accountLink}
        ariaLabel={intl.formatMessage({ id: 'Topbar.login' })}
        title={intl.formatMessage({ id: 'Topbar.login' })}
      >
        <NavigationIcon variant="account" />
      </NamedLink>
    </>
  );

  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      {/* Uroborox imagotipo — ignores Console logo asset */}
      <NamedLink name="LandingPage" className={css.logoLink}>
        <img src={imagotipoImg} alt="Uroborox" className={css.logoImg} />
      </NamedLink>

      {/* Spacer to push nav links to the right */}
      <div className={css.spacer} />

      <ExploreMenu />
      {createListingLinkMaybe}
      {inboxLinkMaybe}
      {profileMenuMaybe}
      {authLinks}
    </nav>
  );
};

export default TopbarDesktop;
